import React, { useState, useEffect } from "react";
import CustomSwiper from "../../../../components/Slider/CustomSwiper";
import "swiper/swiper-bundle.css";
import "./EntranceExams.scss";
import { Link } from "react-router-dom";

interface Cards {
  examName: string;
  iconUrl: string;
  collegesParticipating: string;
  examDate: string;
  examLevel: string;
  slug: string;
}

// Keep mockEntranceExamsData as fallback
const mockEntranceExamsData: Cards[] = [
  {
    examName: "SRM JEE – 2025",
    iconUrl: "/images/entrance-exams-jee-icon.webp",
    collegesParticipating: "110",
    examDate: "27 Apr 2025",
    examLevel: "National",
    slug: "srm-jee-2025",
  },
  {
    examName: "VITEEE – 2025",
    iconUrl: "/images/entrance-exams-jee-icon.webp",
    collegesParticipating: "80",
    examDate: "21 Apr 2025",
    examLevel: "National",
    slug: "viteee-2025",
  },
  {
    examName: "BITSAT – 2025",
    iconUrl: "/images/entrance-exams-jee-icon.webp",
    collegesParticipating: "60",
    examDate: "15 May 2025",
    examLevel: "National",
    slug: "bitsat-2025",
  },
  {
    examName: "MHT CET – 2025",
    iconUrl: "/images/entrance-exams-jee-icon.webp",
    collegesParticipating: "150",
    examDate: "10 May 2025",
    examLevel: "State",
    slug: "mht-cet-2025",
  },
  {
    examName: "WBJEE – 2025",
    iconUrl: "/images/entrance-exams-jee-icon.webp",
    collegesParticipating: "90",
    examDate: "28 Apr 2025",
    examLevel: "State",
    slug: "wbjee-2025",
  },
  {
    examName: "COMEDK UGET – 2025",
    iconUrl: "/images/entrance-exams-jee-icon.webp",
    collegesParticipating: "100",
    examDate: "12 May 2025",
    examLevel: "State",
    slug: "comedk-uget-2025",
  },
  {
    examName: "KIITEE – 2025",
    iconUrl: "/images/entrance-exams-jee-icon.webp",
    collegesParticipating: "30",
    examDate: "05 Apr 2025",
    examLevel: "National",
    slug: "kiitee-2025",
  },
  {
    examName: "AP EAMCET – 2025",
    iconUrl: "/images/entrance-exams-jee-icon.webp",
    collegesParticipating: "200",
    examDate: "19 Apr 2025",
    examLevel: "State",
    slug: "ap-eamcet-2025",
  },
];

const DEFAULT_EXAM_ICON =  "/images/college-predictor/predictor-icon.png";

const ExamCard: React.FC<Cards> = ({
  examName,
  iconUrl,
  collegesParticipating,
  examDate,
  examLevel,
  slug,
}) => (
  <div className="flex flex-col pt-4 bg-white rounded-xl w-full md:w-[360px] lg:w-[413px] pb-4 mx-auto">
    <div className="flex flex-row gap-2 pt-3 pl-3 pb-5">
      <img
        loading="lazy"
        src={iconUrl}
        className="shrink-0 max-w-full rounded-md aspect-[1.82] w-[50px] h-[50px] mr-2"
        alt={examName}
        onError={(e) => {
          const target = e.target as HTMLImageElement;
          target.src = DEFAULT_EXAM_ICON;
        }}
      />
      <div className="examName my-auto">{examName}</div>
    </div>
    <div className="flex flex-row gap-4 px-4 pb-2 text-[#7C7C7C] text-sm font-normal leading-6 min-max-xss:text-[11px]">
      <div className="flex flex-col border-r border-[#7C7C7C] pr-2">
        <div>College participating</div>
        <div>{collegesParticipating}</div>
      </div>
      <div className="flex flex-col border-r border-[#7C7C7C] pr-2">
        <div>Exam Date</div>
        <div className="text-[#173CBA]">{examDate}</div>
      </div>
      <div className="flex flex-col">
        <div>Exam Level</div>
        <div className="text-[#173CBA]">{examLevel}</div>
      </div>
    </div>
    <div className="mx-4 border border-[#7C7C7C] opacity-50"></div>
    <div className="text-[#7C7C7C] text-sm font-normal leading-6 min-max-xss:text-[11px] flex flex-row gap-4 px-4 pt-2 ">
      <Link to={`/exam-details/${slug}`}>
        <div className="border-r border-[#7C7C7C] pr-2 xl:pr-4">Exam Info</div>
      </Link>
      <Link to={`/exam-details/${slug}#result-n-cut-off`}>
        <div className="border-r border-[#7C7C7C] pr-2 xl:pr-4">Cutoff</div>
      </Link>
      <Link to={`/exam-details/${slug}?tab=practice`}>
        <div className="border-r border-[#7C7C7C] pr-2 xl:pr-4">
          Practice Test
        </div>
      </Link>
      <Link to={`/college-predictor/${slug}`}>
        <div>Predict Now</div>
      </Link>
    </div>
  </div>
);

const useWindowWidth = () => {
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);
  return windowWidth;
};

interface EntranceExamsProps {
  entranceExamsData?: any[];
}

const EntranceExams: React.FC<EntranceExamsProps> = ({ entranceExamsData = [] }) => {
  const windowWidth = useWindowWidth();
  const isNarrowScreen = windowWidth < 768;

  // Use provided data or fallback to mock data if empty
  const examsData = entranceExamsData.length > 0 
    ? entranceExamsData.map(exam => ({
        examName: exam.examName || "",
        iconUrl: exam.iconUrl || DEFAULT_EXAM_ICON,
        collegesParticipating: exam.collegesParticipating || "100+",
        examDate: exam.examDate || "Coming Soon",
        examLevel: exam.examLevel || "National",
        slug: exam.slug || "",
      }))
    : mockEntranceExamsData;

  const groupCards = (cards: Cards[], isNarrow: boolean): Cards[][] => {
    const grouped: Cards[][] = [];
    const groupSize = isNarrow ? 1 : 2;
    for (let i = 0; i < cards.length; i += groupSize) {
      grouped.push(cards.slice(i, i + groupSize));
    }
    return grouped;
  };

  const groupedCards = groupCards(examsData, isNarrowScreen);

  const slidesContent = groupedCards.map((group, index) => (
    <div key={index} className="flex flex-col gap-8">
      {group.map((card, subIndex) => (
        <ExamCard key={subIndex} {...card} />
      ))}
    </div>
  ));

  return (
    <div className="bg-[#EEFFFF] min-h-[329px] my-7 pb-6">
      <h3 className="news-alerts-title text-center pt-9 max-md:pt-5 mb-10 max-md:mb-3 max-md:px-14 text-[#383838] text-[40px] max-md:text-[32px] font-medium">
        Entrance Exams in India - 2025
      </h3>
      <div className="container">
        <CustomSwiper
          className="!px-2"
          items={slidesContent}
          navigation="bottom"
          autoplay={true}
          breakpoints={{
            320: { slidesPerView: 1, spaceBetween: 20 },
            768: { slidesPerView: 2, spaceBetween: 20 },
            1024: { slidesPerView: 3, spaceBetween: 20 },
            1280: { slidesPerView: 3, spaceBetween: 30 },
          }}
          progressBar={true}
        />
      </div>
    </div>
  );
};

export default EntranceExams;
